import React, { useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { howItWorks } from '../content';
import { FreezeContext, Stage, cx, labelOf, useClock, useOnScreen, type ExplainerProps, type SceneDef } from './engine';
import { resolveUnit } from './unit';
import { tagDef } from './Tag';
import { handoverDef } from './Handover';
import { ownerScanDef } from './OwnerScan';

const DEFS: SceneDef[] = [tagDef, handoverDef, ownerScanDef];

const CSS = `
.xps{container-type:inline-size;font-family:Poppins,Arial,sans-serif}
.xps-tabs{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}
.xps-tab{appearance:none;background:none;border:0;padding:0 0 6px;text-align:left;cursor:pointer;color:inherit;font:inherit}
.xps-track{display:block;height:3px;border-radius:3px;overflow:hidden;background:var(--xps-track)}
.xps-fill{display:block;height:100%;background:var(--xps-fill);transition:width 60ms linear}
.xps-lab{display:flex;align-items:baseline;gap:8px;margin-top:12px;font-weight:600;font-size:15px;letter-spacing:-.01em;color:var(--xps-muted);transition:color .2s}
.xps-tab[aria-selected="true"] .xps-lab{color:var(--xps-fg)}
.xps-tab:hover .xps-lab{color:var(--xps-fg)}
.xps-n{font-family:'Geist Mono',ui-monospace,monospace;font-size:11.5px;font-weight:500;letter-spacing:.06em;color:var(--xps-accent)}
.xps-tab:focus-visible{outline:2px solid #2F9E58;outline-offset:4px;border-radius:6px}
.xps-body{display:grid;gap:24px;margin-top:28px;align-items:center}
.xps-copy{order:2}
.xps-title{font-weight:700;font-size:clamp(20px,2.2vw,26px);line-height:1.2;letter-spacing:-.02em;color:var(--xps-fg);margin:0}
.xps-desc{margin:10px 0 0;font-size:16px;line-height:1.55;color:var(--xps-muted);max-width:42ch}
.xps-copy-in{animation:xps-in .45s cubic-bezier(.215,.61,.355,1) both}
@keyframes xps-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
@container (min-width: 760px){
  .xps-body{grid-template-columns:minmax(0,5fr) minmax(0,7fr);gap:48px;margin-top:40px}
  .xps-copy{order:0}
}
@media (prefers-reduced-motion: reduce){.xps *{transition:none!important;animation:none!important}}
`;
if (typeof document !== 'undefined') {
  let el = document.getElementById('xps-styles') as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = 'xps-styles';
    document.head.appendChild(el);
  }
  el.textContent = CSS;
}

function useReduced() {
  const [r, setR] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    const q = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!q) return;
    const on = () => setR(q.matches);
    q.addEventListener?.('change', on);
    return () => q.removeEventListener?.('change', on);
  }, []);
  return r;
}

/** Ramp-style tabbed sequence: 01 Tag / 02 Hand over / 03 Own. Progress bars fill and auto-advance. */
export function HowItWorksSequence({ tone = 'light', autoPlay = true, className, unit }: ExplainerProps) {
  const u = useMemo(() => resolveUnit(unit), [unit]);
  const [i, setI] = useState(0);
  const reduced = useReduced();
  const freeze = useContext(FreezeContext);
  const [ref, visible] = useOnScreen<HTMLDivElement>(0.3);
  const def = DEFS[i];
  const still = reduced || !autoPlay || freeze !== undefined;
  const { t, seek } = useClock({
    duration: def.duration - 300,
    running: visible,
    loop: false,
    staticAt: still ? (freeze !== undefined ? Math.min(freeze, def.end) : def.end) : undefined,
    onEnd: () => {
      seek(0);
      setI((x) => (x + 1) % DEFS.length);
    },
  });
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  const uid = useId().replace(/:/g, '');
  const dark = tone === 'dark';
  const vars = {
    '--xps-fg': dark ? '#fff' : '#241E61',
    '--xps-muted': dark ? 'rgba(255,255,255,.62)' : '#67648F',
    '--xps-accent': dark ? '#CFF710' : '#2F9E58',
    '--xps-track': dark ? 'rgba(255,255,255,.16)' : '#E4E3F0',
    '--xps-fill': dark ? '#CFF710' : 'linear-gradient(95deg, #3FAE6C 0%, #8FD53A 55%, #CFF710 100%)',
  } as React.CSSProperties;

  const select = (n: number, focus = false) => {
    const k = (n + DEFS.length) % DEFS.length;
    if (!still) seek(0);
    setI(k);
    if (focus) tabs.current[k]?.focus();
  };
  const onKey = (e: React.KeyboardEvent) => {
    const map: Record<string, number> = { ArrowRight: i + 1, ArrowDown: i + 1, ArrowLeft: i - 1, ArrowUp: i - 1, Home: 0, End: DEFS.length - 1 };
    if (e.key in map) {
      e.preventDefault();
      select(map[e.key], true);
    }
  };
  const step = howItWorks.steps[i];
  const fill = still ? 1 : Math.min(1, t / (def.duration - 300));
  const S = def.Scene;

  return (
    <div ref={ref} className={cx('xps', className)} style={vars}>
      <div role="tablist" aria-label="How it works" className="xps-tabs" onKeyDown={onKey}>
        {howItWorks.steps.map((s, n) => (
          <button
            key={s.n}
            ref={(el) => (tabs.current[n] = el)}
            role="tab"
            type="button"
            id={`${uid}-tab-${n}`}
            aria-selected={n === i}
            aria-controls={`${uid}-panel`}
            tabIndex={n === i ? 0 : -1}
            className="xps-tab"
            onClick={() => select(n)}
          >
            <span className="xps-track">
              <span className="xps-fill" style={{ width: n === i ? `${fill * 100}%` : '0%' }} />
            </span>
            <span className="xps-lab">
              <span className="xps-n">{s.n}</span>
              {s.k}
            </span>
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`${uid}-panel`} aria-labelledby={`${uid}-tab-${i}`} className="xps-body">
        <div className="xps-copy">
          <div key={i} className="xps-copy-in">
            <h3 className="xps-title">{step.t}</h3>
            <p className="xps-desc">{step.d}</p>
          </div>
        </div>
        <div key={i}>
          <Stage W={def.W} H={def.H} tone={tone} t={t} duration={def.duration} loop={false} label={labelOf(def, u)}>
            <S t={t} tone={tone} u={u} />
          </Stage>
        </div>
      </div>
    </div>
  );
}
